import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useSchedule } from "./ScheduleModal";
import { BrandLogo } from "./BrandLogo";

const LINKS = [
  { label: "About", hash: "about" },
  { label: "Coverages", hash: "products" },
  { label: "Quote", hash: "quote" },
  { label: "Contact", hash: "contact" },
];

// Route-aware section link: smooth-scroll anchor on the homepage, else navigate
// home (client-side) and let SmoothScroll scroll to the section on mount.
const NavItem = ({ isHome, hash, testid, className, onClick, children }) =>
  isHome ? (
    <a href={`#${hash}`} data-testid={testid} className={className} onClick={onClick}>{children}</a>
  ) : (
    <Link to={`/#${hash}`} data-testid={testid} className={className} onClick={onClick}>{children}</Link>
  );

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { openSchedule } = useSchedule();
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="navbar"
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-[#030303]/75 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
    >
      <nav className="max-w-[1400px] mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        {isHome ? (
          <a href="#top" data-testid="brand-logo" className="group">
            <BrandLogo size="lg" crestClassName="group-hover:scale-105 transition-transform duration-400" />
          </a>
        ) : (
          <Link to="/" data-testid="brand-logo" className="group">
            <BrandLogo size="lg" crestClassName="group-hover:scale-105 transition-transform duration-400" />
          </Link>
        )}

        <div className="hidden md:flex items-center gap-6 lg:gap-8">
          {LINKS.map((l) => (
            <NavItem
              key={l.hash}
              isHome={isHome}
              hash={l.hash}
              testid={`nav-${l.label.toLowerCase()}`}
              className="mono text-xs uppercase tracking-[0.2em] text-[#a3a3a3] hover:text-[#f4f0ea] link-underline transition-colors duration-300"
            >
              {l.label}
            </NavItem>
          ))}
          <button
            onClick={openSchedule}
            data-testid="nav-schedule"
            className="mono text-xs uppercase tracking-[0.2em] text-[#a3a3a3] hover:text-[#f4f0ea] link-underline transition-colors duration-300"
          >
            Schedule Consultation
          </button>
          <NavItem
            isHome={isHome}
            hash="quote"
            testid="nav-cta"
            className="mono text-xs uppercase tracking-[0.2em] px-6 py-3 border border-[#d4af37]/50 text-[#d4af37] hover:bg-[#d4af37] hover:text-[#030303] transition-colors duration-400 rounded-full"
          >
            Get a Quote
          </NavItem>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden text-[#f4f0ea]"
          aria-label="Menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-[#030303]/95 backdrop-blur-xl border-b border-white/10"
          >
            <div className="px-6 py-8 flex flex-col gap-6">
              {LINKS.map((l) => (
                <NavItem
                  key={l.hash}
                  isHome={isHome}
                  hash={l.hash}
                  onClick={() => setOpen(false)}
                  testid={`mobile-nav-${l.label.toLowerCase()}`}
                  className="mono text-sm uppercase tracking-[0.2em] text-[#a3a3a3]"
                >
                  {l.label}
                </NavItem>
              ))}
              <button
                onClick={() => { setOpen(false); openSchedule(); }}
                data-testid="mobile-nav-schedule"
                className="mono text-sm uppercase tracking-[0.2em] text-[#a3a3a3] text-left"
              >
                Schedule Consultation
              </button>
              <NavItem
                isHome={isHome}
                hash="quote"
                onClick={() => setOpen(false)}
                testid="mobile-nav-cta"
                className="mono text-sm uppercase tracking-[0.2em] text-[#d4af37]"
              >
                Get a Quote →
              </NavItem>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
